import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { uuid } from 'src/utilities/text.js';
import '../library/components/d-help-section.js';
import './assets/d-asset-view.js';
import './contacts/d-contact-view.js';
import './contracts/d-contract-view.js';
import './competence/d-competence-view.js';
import './documents/d-document-view.js';
import './employees/d-employee-view.js';
import './event-occurrences/d-event-occurrence-view';
import './meeting-occurrences/d-meeting-occurrence-view';
import './functions/d-function-view.js';
import './guidelines/d-guideline-view.js';
import './issues/d-issue-view.js';
import './meetings/d-meeting-view.js';
import './partners/d-partner-view.js';
import './reports/d-report-view.js';
import './summaries/d-summary-view';
import './risk-assessments/d-risk-assessment-view.js';
import './substances/d-substance-view.js';
import './tasks/d-task-view.js';
import './tutorials/d-tutorial-view.js';
import './screens/d-screen-view';
import 'src/content/collections/d-collection-view';
import type { DocumentViewEditItem } from './documents/document-view-edit-item';
import type { AfterSaveHandler } from './after-save-handler';
import type { ContentView } from './content-view';
import type { ContentEditDoneDetail } from './content-edit-done-detail';
import type { AssetViewEditItem } from './assets/asset-view-edit-item';
import type { CollectionViewEditItem } from './collections/collection-view-edit-item';
import type { ContactViewEditItem } from './contacts/contact-view-edit-item';
import type { ContractViewEditItem } from './contracts/contract-view-edit-item';
import type { CompetenceViewEditItem } from './competence/competence-view-edit-item';
import type { EmployeeViewEditItem } from './employees/employee-view-edit-item';
import type { SaveType } from './event-occurrences/save-type';
import type { EventOccurrenceViewEditItem } from './event-occurrences/event-occurrence-view-edit-item';
import type { FunctionViewEditItem } from './functions/function-view-edit-item';
import type { GuidelineViewEditItem } from './guidelines/guideline-view-edit-item';
import type { IssueViewEditItem } from './issues/issue-view-edit-item';
import type { MeetingOccurrenceViewEditItem } from './meeting-occurrences/meeting-occurrence-view-edit-item';
import type { PartnerViewEditItem } from './partners/partner-view-edit-item';
import type { ReportViewEditItem } from './reports/report-view-edit-item';
import type { SubstanceViewEditItem } from './substances/substance-view-edit-item';
import type { RiskAssessmentViewEditItem } from './risk-assessments/risk-assessment-view-edit-item';
import type { TaskViewEditItem } from './tasks/task-view-edit-item';

/**
 *
 * Selects the content view to display based on the type of the contentView property.
 *
 *
 *
 */
@customElement('d-content')
export class DContent extends LitElement {
  static readonly styles = css`
    :host {
      flex: 1;
      position: relative;
      direction: ltr;
      background: white;
    }

    section {
      margin-top: -9px;
    }
  `;
  @property({ type: Number })
  pageId = 0;
  @property({ type: Number })
  level = 0;
  @property({ type: String })
  route = '';
  @property({ type: Number })
  count = 0;
  @property({ type: Object })
  user: any = {};
  @property({ type: Object })
  organization: any = {};
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Object })
  employeeListConfig: any = {};
  @property({ type: Object })
  issueListConfig: any = {};
  @property({ type: Object })
  entityRoute: any = {};
  @property({ type: Object })
  entityItem: any = {};
  @property({ type: String })
  entityId = '';
  @property({ type: String })
  entityType = '';
  @property({ type: String })
  assetUuidForNewEvent = '';
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: Object })
  contentView!: ContentView;
  @property({ type: Boolean })
  covered = false;
  @property({ type: String })
  parentHref = '';
  @property({ type: String })
  mode = 'normal';
  @property({ type: Boolean, attribute: 'prevent-header-shrink' })
  preventHeaderShrink = false;
  @property({ type: Boolean })
  storybook = false;

  private get stickyTop(): number {
    return 0;
  }

  render() {
    const view = this.contentView;
    switch (view.type) {
      case 'empty':
        // Return nothing for empty views (e.g., after deletion)
        return nothing;
      case 'issues':
        return html`
          <d-issue-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(e: CustomEvent<{ afterSave?: AfterSaveHandler; editItem: IssueViewEditItem }>) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-issue-view>
        `;
      case 'assets':
        return html`
          <d-asset-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            duplicatable
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(e: CustomEvent<{ afterSave?: AfterSaveHandler; editItem: AssetViewEditItem }>) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: AssetViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-asset-view>
        `;
      case 'constitutionalDocuments':
      case 'contracts':
        return html`
          <d-contract-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            duplicatable
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: ContractViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: ContractViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-contract-view>
        `;
      case 'competence':
        return html`
          <d-competence-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: CompetenceViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-competence-view>
        `;
      case 'documents':
        return html`
          <d-document-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            duplicatable
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: DocumentViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: DocumentViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-document-view>
        `;
      case 'employees':
        return html`
          <d-employee-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: EmployeeViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-employee-view>
        `;
      case 'eventOccurrences':
        return html`
          <d-event-occurrence-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @checklist-changed=${(e: CustomEvent<{ content: string }>) => {
              e.stopPropagation();
              // Only dispatch if we have a valid uuid (eventOccurrenceId)
              if (!view.uuid) {
                console.warn('Checklist changed but event occurrence UUID not available, skipping update');
                return;
              }
              this.dispatchEvent(
                new CustomEvent<{ uuid: string; content: string }>('checklist-changed', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    uuid: view.uuid,
                    content: e.detail.content,
                  },
                }),
              );
            }}
            @delete-event=${(e: CustomEvent<{ saveType: SaveType; alert: boolean }>) => {
              e.stopPropagation();
              this.dispatchEvent(
                new CustomEvent<{
                  url: string;
                  type: string;
                  uuid: string;
                  saveType: string;
                  alert: boolean;
                }>('content-delete-event', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    type: view.type,
                    uuid: view.uuid,
                    url: view.parentHref,
                    saveType: e.detail.saveType,
                    alert: e.detail.alert,
                  },
                }),
              );
            }}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: EventOccurrenceViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.dispatchEvent(
                new CustomEvent<ContentEditDoneDetail>('content-save-event', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    type: view.type,
                    uuid: view.uuid,
                    editItem: e.detail.editItem,
                    afterSave: e.detail.afterSave,
                    saveType: e.detail.editItem.saveType,
                    alert: e.detail.editItem.alert,
                    message: e.detail.editItem.message,
                    includeDetailsInAlert: e.detail.editItem.includeDetailsInAlert,
                  },
                }),
              );
            }}
          >
          </d-event-occurrence-view>
        `;
      case 'meetingOccurrences':
        return html`
          <d-meeting-occurrence-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @checklist-changed=${(e: CustomEvent<{ content: string }>) => {
              e.stopPropagation();
              // Only dispatch if we have a valid uuid (meetingOccurrenceId)
              if (!view.uuid) {
                console.warn('Checklist changed but meeting occurrence UUID not available, skipping update');
                return;
              }
              this.dispatchEvent(
                new CustomEvent<{ uuid: string; content: string }>('checklist-changed', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    uuid: view.uuid,
                    content: e.detail.content,
                  },
                }),
              );
            }}
            @delete-meeting-occurrence=${(e: CustomEvent<{ saveType: SaveType; alerts: boolean }>) => {
              e.stopPropagation();
              this.dispatchEvent(
                new CustomEvent<{
                  url: string;
                  type: string;
                  uuid: string;
                  saveType: string;
                  alerts: boolean;
                }>('content-delete-meeting-occurrence', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    type: view.type,
                    uuid: view.uuid,
                    url: view.parentHref,
                    saveType: e.detail.saveType,
                    alerts: e.detail.alerts,
                  },
                }),
              );
            }}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: MeetingOccurrenceViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.dispatchEvent(
                new CustomEvent<ContentEditDoneDetail>('content-save-meeting-occurrence', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    type: view.type,
                    uuid: view.uuid,
                    editItem: e.detail.editItem,
                    afterSave: e.detail.afterSave,
                    saveType: e.detail.editItem.saveType,
                  },
                }),
              );
            }}
          >
          </d-meeting-occurrence-view>
        `;
      case 'functions':
        return html`
          <d-function-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: FunctionViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-function-view>
        `;
      case 'tasks':
        return html`
          <d-task-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(e: CustomEvent<{ afterSave?: AfterSaveHandler; editItem: TaskViewEditItem }>) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          ></d-task-view>
        `;
      case 'guidelines':
        return html`
          <d-guideline-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            duplicatable
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: GuidelineViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: GuidelineViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-guideline-view>
        `;
      case 'meetings':
        return html`
          <d-meeting-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: Record<string, never>;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-meeting-view>
        `;
      case 'partners':
        return html`
          <d-partner-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: PartnerViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-partner-view>
        `;
      case 'contacts':
        return html`
          <d-contact-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: ContactViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-contact-view>
        `;
      case 'summaries':
        return html`
          <d-summary-view
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            .contentStickyTop=${this.stickyTop}
          >
          </d-summary-view>
        `;
      case 'reports':
        return html`
          <d-report-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?duplicatable=${!view.hasRelatedMeetingEvent}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            .isEmployee=${true}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: ReportViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: ReportViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-report-view>
        `;
      case 'riskAssessments':
        return html`
          <d-risk-assessment-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: RiskAssessmentViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
            @save-as-new=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: RiskAssessmentViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onSaveAsNew(e, e.detail.editItem, view);
            }}
          >
          </d-risk-assessment-view>
        `;
      case 'substances':
        return html`
          <d-substance-view
            .stickyTop=${this.stickyTop}
            .entityView=${view}
            .singleUserVersion=${this.singleUserVersion}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: SubstanceViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-substance-view>
        `;
      case 'tutorials':
        return html`
          <d-tutorial-view
            .stickyTop=${this.stickyTop}
            .singleUserVersion=${this.singleUserVersion}
            .entityView=${view}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
          >
          </d-tutorial-view>
        `;
      case 'screens':
        return html`
          <d-screen-view
            .stickyTop=${this.stickyTop}
            .singleUserVersion=${this.singleUserVersion}
            .entityView=${view}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: CollectionViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-screen-view>
        `;
      case 'collections':
        return html`
          <d-collection-view
            .stickyTop=${this.stickyTop}
            .singleUserVersion=${this.singleUserVersion}
            .entityView=${view}
            .pageId=${this.pageId}
            .mode=${this.mode}
            .parentHref=${this.parentHref}
            ?covered=${this.covered}
            .preventHeaderShrink=${this.preventHeaderShrink}
            @entity-delete=${this.onEntityDelete}
            @done=${(
              e: CustomEvent<{
                afterSave?: AfterSaveHandler;
                editItem: CollectionViewEditItem;
              }>,
            ) => {
              e.stopPropagation();
              this.onDone(e, e.detail.editItem, view);
            }}
          >
          </d-collection-view>
        `;
    }
  }

  private onDone(e, editItem, view) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent<ContentEditDoneDetail>('content-edit-done', {
        bubbles: true,
        composed: true,
        detail: {
          type: view.type,
          uuid: view.uuid,
          editItem: editItem,
          afterSave: e.detail.afterSave,
        },
      }),
    );
  }

  private onSaveAsNew(e, editItem, view) {
    const newUuid = uuid();
    const destination = view.parentHref + '/' + view.type + '/' + newUuid + '?edit';
    this.dispatchEvent(new CustomEvent('edit', { bubbles: true, composed: true }));
    this.dispatchEvent(
      new CustomEvent('navigate', {
        bubbles: true,
        composed: true,
        detail: { href: destination },
      }),
    );
    this.dispatchEvent(
      new CustomEvent<ContentEditDoneDetail>('content-edit-done', {
        bubbles: true,
        composed: true,
        detail: {
          type: view.type,
          uuid: newUuid,
          editItem: editItem,
          afterSave: e.detail.afterSave,
        },
      }),
    );
  }

  private onEntityDelete(e: Event) {
    e.stopPropagation();
    if (this.contentView.type !== 'empty') {
      this.dispatchEvent(
        new CustomEvent<{ type: string; uuid: string; url: string }>('entity-delete', {
          bubbles: true,
          composed: true,
          detail: {
            type: this.contentView.type,
            uuid: this.contentView.uuid,
            url: this.contentView.parentHref,
          },
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-content': DContent;
  }
}
